import type { PersistedPoiCategoryDraft } from '@/entities/poi'
import type { ISurveyTranslationRow } from '@/features/survey/survey-translation-section'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from '@/shared/libs/generate-path'
import {
  buildPoiCategoryDraft,
  hasPoiCategoryDraftContent,
  useCreatePoiCategoryMutation,
  usePoiCategoriesQuery,
} from '@/entities/poi'
import { useTenantByIdQuery, useTenantLanguages } from '@/entities/tenant'
import { useCreateTranslationDraft } from '@/features/localized-translation'
import { PoiCategoryForm } from '@/features/poi'
import { TRANSLATION_JOB_SCOPE } from '@/shared/constants/status'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { hasRequiredTranslations, resolveDefaultLanguages } from '@/shared/utils/language-utils'

const DEFAULT_POI_CATEGORY_COLOR = '#000000'

function mergeCategoryTranslationRows(languages: string[], rows?: ISurveyTranslationRow[]) {
  const byLanguage = new Map((rows ?? []).map((row) => [row.language, row]))
  return languages.map((language) => ({
    language,
    name: byLanguage.get(language)?.name ?? '',
    description: '',
  }))
}

function buildLocalizedDataPayload(rows: ISurveyTranslationRow[]) {
  return rows.reduce<Record<string, { name: string }>>((acc, row) => {
    const name = row.name.trim()
    if (name) {
      acc[row.language] = { name }
    }
    return acc
  }, {})
}

export function PoiCategoryCreatePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams({ strict: false })
  const search: { returnTo?: unknown } = useSearch({ strict: false })
  const scopedAdminPath = useScopedAdminPath()
  const { activeTenantId = '', setActiveTenantId } = useAdminScope()
  const tenantId = params.tenantId ?? params.id ?? ''
  const listPath = scopedAdminPath(generatePath('/tenant/:tenantId/poi-categories', { tenantId }))
  const returnTo = typeof search.returnTo === 'string' ? search.returnTo.trim() : ''

  const { data: tenantResponse, isLoading: tenantLoading } = useTenantByIdQuery(tenantId)
  const { items: tenantLanguageItems } = useTenantLanguages(tenantId)
  const createMutation = useCreatePoiCategoryMutation()
  const categoriesQuery = usePoiCategoriesQuery(tenantId, {})

  const languageCodes = useMemo(
    () => resolveDefaultLanguages(tenantLanguageItems),
    [tenantLanguageItems],
  )
  const translationEntityId = useMemo(
    () => `poi-category-create:${tenantId || 'unknown-tenant'}`,
    [tenantId],
  )
  const [translations, setTranslations] = useState<ISurveyTranslationRow[]>([])
  const [icon, setIcon] = useState('')
  const [iconActive, setIconActive] = useState('')
  const [color, setColor] = useState(DEFAULT_POI_CATEGORY_COLOR)
  const [isIconUploading, setIsIconUploading] = useState(false)
  const [isIconActiveUploading, setIsIconActiveUploading] = useState(false)
  const categoryDraft = useMemo(
    () => buildPoiCategoryDraft({ icon, iconActive, color, translations }),
    [color, icon, iconActive, translations],
  )
  const hasCategoryDraftContent = useMemo(
    () => hasPoiCategoryDraftContent({ icon, iconActive, color, translations }),
    [color, icon, iconActive, translations],
  )
  const hydrateCategoryDraft = useCallback(
    (persistedDraft?: PersistedPoiCategoryDraft) => {
      setIcon(persistedDraft?.icon ?? '')
      setIconActive(persistedDraft?.iconActive ?? '')
      setColor(persistedDraft?.color ?? DEFAULT_POI_CATEGORY_COLOR)
      setTranslations(mergeCategoryTranslationRows(languageCodes, persistedDraft?.translations))
    },
    [languageCodes],
  )
  const { clearCurrentDraft, clearCurrentJobs } = useCreateTranslationDraft({
    enabled: true,
    scope: TRANSLATION_JOB_SCOPE.POI_CATEGORY,
    entityId: translationEntityId,
    audioEnabled: false,
    initSignature: `${translationEntityId}:${languageCodes.join('\0')}`,
    draft: categoryDraft,
    hasContent: hasCategoryDraftContent,
    onHydrate: hydrateCategoryDraft,
  })

  useEffect(() => {
    if (!tenantId || activeTenantId === tenantId) {
      return
    }
    setActiveTenantId(tenantId)
  }, [activeTenantId, setActiveTenantId, tenantId])

  const canValidateDuplicateCategories = categoriesQuery.isSuccess
  const isSubmitDisabled =
    createMutation.isPending ||
    isIconUploading ||
    isIconActiveUploading ||
    languageCodes.length < 1 ||
    !canValidateDuplicateCategories

  async function handleSubmit() {
    if (isIconUploading || isIconActiveUploading) {
      showToast.error(
        t('poi.media.waitForUpload', {
          defaultValue: 'Please wait for image upload to finish before saving.',
        }),
      )
      return
    }
    if (!hasRequiredTranslations(translations, ['name'], languageCodes)) {
      showToast.error(t('poiCategories.allLanguagesRequired'))
      return
    }
    if (!canValidateDuplicateCategories) {
      showToast.error(
        t('poiCategories.loadFailed', {
          defaultValue: 'Could not load categories to validate duplicates. Please try again.',
        }),
      )
      return
    }
    const localizedData = buildLocalizedDataPayload(translations)
    const duplicate = categoriesQuery.data.data?.some((item) =>
      Object.entries(localizedData).some(([language, value]) => {
        const existing = (item.localizedData?.[language]?.name ?? '').trim()
        return existing.toLowerCase() === value.name.toLowerCase()
      }),
    )
    if (duplicate) {
      showToast.error(
        t('poiCategories.nameDuplicate', { defaultValue: 'Category name already exists' }),
      )
      return
    }
    try {
      await createMutation.mutateAsync({
        ...(icon.trim() ? { icon: icon.trim() } : {}),
        ...(iconActive.trim() ? { iconActive: iconActive.trim() } : {}),
        color,
        localizedData,
      })
      showToast.success(t('poiCategories.createSuccess'))
      clearCurrentJobs()
      clearCurrentDraft()
      await navigate({ href: returnTo || listPath })
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  const tenantName = tenantResponse?.data?.name ?? tenantId
  return (
    <PageContainer>
      <PageHeader
        title={t('poiCategories.create')}
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          ...(tenantId
            ? [
                {
                  label: tenantLoading ? t('common.loading') : tenantName || tenantId,
                  path: scopedAdminPath(generatePath('/tenant/:tenantId', { tenantId })),
                },
              ]
            : []),
          { label: t('poiCategories.title'), path: listPath },
          { label: t('common.create', { defaultValue: 'Create' }) },
        ]}
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void navigate({ href: listPath })
            }}
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </Button>
        }
      />

      <PoiCategoryForm
        icon={icon}
        iconActive={iconActive}
        color={color}
        translations={translations}
        languageCodes={languageCodes}
        translationEntityId={translationEntityId}
        entityLabel={t('poiCategories.create')}
        isSubmitting={createMutation.isPending}
        isSubmitDisabled={isSubmitDisabled}
        onIconChange={setIcon}
        onColorChange={setColor}
        onIconUploadingChange={setIsIconUploading}
        onIconActiveChange={setIconActive}
        onIconActiveUploadingChange={setIsIconActiveUploading}
        onTranslationsChange={setTranslations}
        onSubmit={handleSubmit}
      />
    </PageContainer>
  )
}
